import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Shield, RefreshCw, FileText, AlertTriangle, UserX, Lock, ScanLine, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';
import StagingExternalRecipients from './StagingExternalRecipients';
import { fetchRootStatus, NO_ROOT_STATUS, rootFetch } from '../../lib/rootSecurity';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// EMT SuperAdmin (2026-06-17): real platform security posture only. Every button
// performs a real action: Run Security Scan re-computes /security/summary;
// Vulnerability Assessment now calls the real backend assessment authority; Export Audit Logs
// downloads real /super-admin/audit-logs; View Audit Logs navigates. No fabricated
// scores/CPU/sessions/vulnerabilities.

const Stat = ({ icon: Icon, label, value, color, loading }) => (
  <Card className="bg-white shadow-md"><CardContent className="p-5 flex items-center justify-between">
    <div><p className="text-sm text-gray-600 mb-1">{label}</p><p className="text-2xl font-bold text-gray-900">{loading ? '…' : value}</p></div>
    <div className={`${color} p-3 rounded-lg`}><Icon className="h-6 w-6 text-white" /></div>
  </CardContent></Card>
);

const SecurityCompliance = () => {
  const { superAdmin } = useSuperAdminAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [assessError, setAssessError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  // Root tier, resolved by the server. Starts as NO_ROOT_STATUS so the Root-only section is not
  // drawn during the first paint, and stays that way on any failure — fetchRootStatus fails closed.
  const [rootStatus, setRootStatus] = useState(NO_ROOT_STATUS);

  const apiRef = useRef(null);
  if (!apiRef.current) {
    const instance = axios.create({ baseURL: BACKEND_URL });
    instance.interceptors.request.use((config) => {
      const token = superAdmin?.token || localStorage.getItem('superadmin_session') ||
        localStorage.getItem('superadmin_session_token') || localStorage.getItem('superAdminToken') ||
        localStorage.getItem('superadminToken');
      if (token) { config.headers = config.headers || {}; config.headers.Authorization = `Bearer ${token}`; }
      return config;
    });
    apiRef.current = instance;
  }
  const api = apiRef.current;

  const fetchSummary = async () => {
    setLoading(true); setError(null);
    try { const r = await api.get('/api/v1/super-admin/security/summary'); setData(r.data); return r.data; }
    catch (err) { setError(err?.response?.status ? `HTTP ${err.response.status}` : 'network error'); setData(null); return null; }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchSummary(); }, []);
  useEffect(() => { let live = true;
    fetchRootStatus().then((st) => { if (live) setRootStatus(st); });
    return () => { live = false; }; }, []);

  // VULNERABILITY ASSESSMENT — a real backend assessment over canonical evidence.
  //
  // This previously derived findings IN THE BROWSER by comparing five posture counters against zero.
  // Two things were wrong with that: it never examined a security CONTROL, and when the summary
  // failed or every counter happened to be zero it rendered "No issues found", which is absence
  // reported as health. The server now assesses named controls and returns UNKNOWN — never PASS —
  // for anything it cannot evidence.
  const runAssessment = async () => {
    setBusy('assess'); setError(null); setAssessError(null);
    try {
      const res = await rootFetch('/api/v1/super-admin/security/assessment', { method: 'POST' });
      const a = res && res.assessment;
      if (!a || !Array.isArray(a.findings)) throw new Error('Malformed assessment response');
      setAssessment(a);
    } catch (err) {
      // A failed assessment must NOT read as a clean one. No findings are rendered on failure.
      setAssessment(null);
      setAssessError(typeof err.detail === 'string' ? err.detail
        : (err.status ? `Assessment failed (HTTP ${err.status})` : 'Assessment failed — the server could not be reached'));
    } finally {
      setBusy(null);
    }
  };

  const exportAuditLogs = async () => {
    setBusy('export');
    try {
      const r = await api.get('/api/v1/super-admin/audit-logs?limit=500');
      // M22 — backend returns { audit_logs: [...] }; read the correct key (prior code read .logs/.data → threw on the object).
      const logs = r.data?.audit_logs || r.data?.logs || (Array.isArray(r.data) ? r.data : []);
      const rows = [['timestamp', 'action', 'resource_type', 'resource_id', 'severity', 'user_id']];
      logs.forEach((l) => rows.push([l.timestamp || '', l.action || '', l.resource_type || '', l.resource_id || '', l.severity || '', l.user_id || '']));
      const csv = rows.map((r2) => r2.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'audit-logs.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.status ? `HTTP ${err.response.status}` : 'network error');
    } finally { setBusy(null); }
  };

  const fmt = (v) => (v === null || v === undefined ? '—' : String(v));
  const sevColor = (s) => s === 'critical' ? 'text-red-700' : s === 'high' ? 'text-red-600'
    : s === 'medium' ? 'text-amber-600' : s === 'low' ? 'text-gray-600' : 'text-gray-400';
  // UNKNOWN is styled as a WARNING, never as a success — the colour must not imply a pass.
  const statusCls = (st) => ({
    PASS: 'bg-green-100 text-green-800',
    FAIL: 'bg-red-100 text-red-800',
    OPEN: 'bg-amber-100 text-amber-800',
    UNKNOWN: 'bg-yellow-100 text-yellow-900',
    REPORT_ONLY: 'bg-blue-100 text-blue-800',
    DEFERRED: 'bg-slate-100 text-slate-700',
    NOT_APPLICABLE: 'bg-gray-100 text-gray-600',
  }[st] || 'bg-gray-100 text-gray-700');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Security &amp; Compliance</h1>
          <p className="text-gray-600 mt-1">Real platform security posture{data?.generated_at ? ` · ${data.generated_at.slice(0, 16).replace('T', ' ')}` : ''}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={fetchSummary} disabled={loading || !!busy}><ScanLine className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Run Security Scan</Button>
          <Button onClick={runAssessment} variant="outline" disabled={!!busy}><Shield className="h-4 w-4 mr-2" />{busy === 'assess' ? 'Assessing…' : 'Vulnerability Assessment'}</Button>
          <Button onClick={exportAuditLogs} variant="outline" disabled={!!busy}><Download className="h-4 w-4 mr-2" />{busy === 'export' ? 'Exporting…' : 'Export Audit Logs'}</Button>
          <Button variant="outline" onClick={() => navigate('/superadmin-portal/dashboard/audit-logs')}><FileText className="h-4 w-4 mr-2" />View Audit Logs</Button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-200">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          <p className="text-sm text-red-700">{error}</p>
          <Button onClick={fetchSummary} size="sm" variant="outline" className="ml-auto">Retry</Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        <Stat icon={Lock} label="Failed Logins (24h)" value={fmt(data?.failed_logins_24h)} color="bg-amber-500" loading={loading} />
        <Stat icon={Shield} label="MFA Coverage" value={data ? `${fmt(data.mfa_coverage_pct)}%` : '—'} color="bg-green-600" loading={loading} />
        <Stat icon={UserX} label="Users Without MFA" value={fmt(data?.users_without_mfa)} color="bg-red-500" loading={loading} />
        <Stat icon={Lock} label="Locked Accounts" value={fmt(data?.locked_accounts)} color="bg-gray-600" loading={loading} />
        <Stat icon={AlertTriangle} label="Suspended Tenants" value={fmt(data?.suspended_tenants)} color="bg-orange-500" loading={loading} />
        <Stat icon={AlertTriangle} label="Open Critical Alerts" value={fmt(data?.critical_alerts_open)} color="bg-red-600" loading={loading} />
      </div>

      <StagingExternalRecipients rootStatus={rootStatus} />

      {assessError && (
        <Card className="bg-white shadow-md" data-testid="assessment-error">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-700">Vulnerability assessment did not complete</p>
              {/* Explicit: a failed assessment is NOT a clean one, and no findings are shown. */}
              <p className="text-xs text-gray-600 mt-0.5">{assessError} — no findings are shown, because a failed assessment is not a passing one.</p>
            </div>
            <Button size="sm" variant="outline" className="ml-auto" onClick={runAssessment} disabled={!!busy}>Retry</Button>
          </CardContent>
        </Card>
      )}

      {assessment && (
        <Card className="bg-white shadow-md" data-testid="assessment-findings">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Vulnerability Assessment</CardTitle>
            <p className="text-xs text-gray-500 mt-1">
              {assessment.summary?.total_controls ?? 0} controls assessed
              {' · '}{assessment.summary?.controls_satisfied ?? 0} satisfied
              {' · '}{assessment.summary?.actionable_findings ?? 0} actionable
              {' · '}{assessment.summary?.controls_unknown ?? 0} unknown
              {assessment.generated_at ? ` · ${String(assessment.generated_at).slice(0, 16).replace('T', ' ')}` : ''}
            </p>
            {(assessment.summary?.controls_unknown ?? 0) > 0 && (
              // Stated plainly on the card: unknown is not a pass, and must not be read as one.
              <p className="text-xs text-amber-700 mt-1">
                {assessment.summary.controls_unknown} control(s) could not be evidenced from data the platform holds.
                Unknown is not a pass — verify these directly.
              </p>
            )}
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {['Status', 'Severity', 'Control', 'Detail', 'Evidence', 'Remediation'].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {assessment.findings.map((f) => (
                  <tr key={f.control} className="hover:bg-gray-50 align-top">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${statusCls(f.status)}`}>{f.status}</span>
                    </td>
                    <td className={`px-3 py-2 text-xs font-semibold uppercase whitespace-nowrap ${sevColor(f.severity)}`}>{f.severity}</td>
                    <td className="px-3 py-2 text-gray-900">{f.title}<div className="text-[10px] text-gray-400 font-mono">{f.control}</div></td>
                    <td className="px-3 py-2 text-gray-700">{f.detail || '—'}</td>
                    <td className="px-3 py-2 text-gray-500 text-xs">{f.evidence_source || '—'}</td>
                    <td className="px-3 py-2 text-gray-700">{f.remediation || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SecurityCompliance;
